import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CoachProfileShell } from '../components/ProfileShell';
import Button from '../components/Button';

export default function CoachVideosPage() {
  const { user, API_URL } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [personalVideoUrl, setPersonalVideoUrl] = useState('');
  const [isUploadedVideo, setIsUploadedVideo] = useState(false);
  const [videoFilename, setVideoFilename] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/coaches/profile`, getAxiosConfig());
      if (response.data) {
        const url = response.data.personal_video_url || '';
        setPersonalVideoUrl(url);
        // Detect if this is an S3 upload (contains /videos/ in the URL)
        if (url && url.includes('/videos/')) {
          setIsUploadedVideo(true);
          setVideoFilename(url.split('/').pop() || 'Uploaded video');
        }
      }
    } catch (error) {
      console.log('Error fetching profile:', error);
    }
  };

  const showMsg = (text, type = '') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 3000);
  };

  const savePersonalVideoUrl = async () => {
    try {
      await axios.post(`${API_URL}/coaches/profile`, { personal_video_url: personalVideoUrl }, getAxiosConfig());
      showMsg('Saved');
    } catch (error) {
      showMsg('Error saving', 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024 * 1024) {
      showMsg('Video must be under 1 GB', 'error');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/coaches/upload-video`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setPersonalVideoUrl(res.data.filepath);
      setIsUploadedVideo(true);
      setVideoFilename(res.data.filename || file.name);
      showMsg('Video uploaded');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Error uploading video', 'error');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = async () => {
    setPersonalVideoUrl('');
    setIsUploadedVideo(false);
    setVideoFilename('');
    try {
      await axios.post(`${API_URL}/coaches/profile`, { personal_video_url: '' }, getAxiosConfig());
      showMsg('Video removed');
    } catch (error) {
      showMsg('Error removing', 'error');
    }
  };

  return (
    <CoachProfileShell activeTab="Videos">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      <div className="settings-section">
        <h2 className="settings-section__title">Personal Video</h2>
        <p className="settings-section__desc">Your main highlight or intro video. Paste a YouTube link or upload a file (max 1 GB).</p>

        {isUploadedVideo ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f1f5f9', borderRadius: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4D8064" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
            <span style={{ flex: 1, fontSize: '14px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {videoFilename}
            </span>
            <Button
              variant="danger"
              size="sm"
              style={{ flexShrink: 0 }}
              onClick={handleRemove}
            >
              Remove
            </Button>
          </div>
        ) : (
          <>
            <div className="form-field">
              <label className="form-field__label" htmlFor="personal_video_url">Video URL</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  id="personal_video_url"
                  type="url"
                  value={personalVideoUrl}
                  onChange={(e) => setPersonalVideoUrl(e.target.value)}
                  onBlur={savePersonalVideoUrl}
                  className="form-field__input"
                  placeholder="https://youtube.com/watch?v=..."
                  style={{ flex: 1 }}
                />
                {personalVideoUrl && (
                  <Button
                    variant="danger"
                    size="sm"
                    style={{ flexShrink: 0 }}
                    onClick={handleRemove}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
              <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>or</span>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
            </div>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Video File'}
              </Button>
              <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '8px' }}>MP4, MOV — max 1 GB</span>
            </div>
          </>
        )}
      </div>
    </CoachProfileShell>
  );
}
